/**
 * Active liveness challenges for staff face enrolment.
 *
 * The threat this addresses is narrow, and worth stating precisely because active liveness is
 * routinely oversold: **it defeats a held-up photograph.** It does not defeat a video replayed
 * on a phone screen, and it does not defeat a deepfake. If the concern is a colleague covering
 * for someone, this is proportionate. If it is an employee actively evading, passive liveness
 * (texture, moire, depth) is needed as well — `svc-identity-verification` already has a
 * `FACE_LIVENESS` check worth reusing rather than reinventing.
 *
 * Three properties separate a real check from a decorative one:
 *
 * 1. **The order is chosen by the server and randomised.** A fixed sequence can be recorded
 *    once and replayed forever.
 * 2. **Each challenge must be held**, not merely touched for one frame. A single fluke frame
 *    during a head sweep would otherwise satisfy "look left".
 * 3. **A return to neutral is required between challenges.** That forces real movement between
 *    poses, which a static image cannot produce, and means a pre-recorded video has to match an
 *    order it could not have known in advance.
 *
 * The module is pure — signals in, state out — so the sequencing rules are unit-testable
 * without a camera.
 */

import type { EnrolmentPolicy, ThresholdPolicy } from '../config.js';
import { offsetPose, type HeadPose } from '../models.js';

export const CHALLENGE_KINDS = [
  'look_left',
  'look_right',
  'look_up',
  'look_down',
  'smile',
] as const;
export type ChallengeKind = (typeof CHALLENGE_KINDS)[number];

export interface ChallengePrompt {
  readonly kind: ChallengeKind;
  readonly title: string;
  /** Phrased from the employee's point of view, never the screen's. */
  readonly instruction: string;
}

export const CHALLENGE_PROMPTS: Readonly<Record<ChallengeKind, ChallengePrompt>> = {
  look_left: {
    kind: 'look_left',
    title: 'Turn your head to your left',
    instruction: 'Turn to your own left and hold it for a moment.',
  },
  look_right: {
    kind: 'look_right',
    title: 'Turn your head to your right',
    instruction: 'Now turn to your own right and hold.',
  },
  look_up: {
    kind: 'look_up',
    title: 'Tilt your head up',
    instruction: 'Look a little above the screen and hold.',
  },
  look_down: {
    kind: 'look_down',
    title: 'Tilt your head down',
    instruction: 'Look down towards your desk and hold.',
  },
  smile: {
    kind: 'smile',
    title: 'Smile',
    instruction: 'Give the camera a clear smile and hold it.',
  },
};

/** Per-frame signals the verifier needs. Missing inputs degrade to "cannot tell". */
export interface ChallengeSignals {
  readonly monotonicTs: number;
  readonly pose: HeadPose | null;
  /** 0..1 smile score from face blendshapes, or null when blendshapes are unavailable. */
  readonly smileScore: number | null;
  readonly faceDetected: boolean;
  readonly faceCount: number;
  readonly confidence: number;
}

export interface ChallengeCheck {
  readonly satisfied: boolean;
  /** 0..1 — how close this frame is to satisfying the challenge, for a progress indicator. */
  readonly progress: number;
  readonly reason: string;
}

/** True when the frame is usable at all: exactly one confident face. */
export function frameIsUsable(
  signals: ChallengeSignals,
  policy: EnrolmentPolicy,
): { usable: boolean; reason: string } {
  if (!signals.faceDetected) return { usable: false, reason: 'no face visible to the camera' };
  if (signals.faceCount > 1) {
    // Enrolment binds a face to a named account. Doing that with two faces in frame risks
    // binding the wrong one, and the mistake would persist.
    return {
      usable: false,
      reason: 'more than one face is visible; only one person may be in frame during enrolment',
    };
  }
  if (signals.confidence < policy.minFrameConfidence) {
    return { usable: false, reason: 'the camera view is not clear enough to verify' };
  }
  return { usable: true, reason: '' };
}

/** Evaluate one frame against one challenge. */
export function verifyChallenge(
  kind: ChallengeKind,
  signals: ChallengeSignals,
  policy: EnrolmentPolicy,
): ChallengeCheck {
  const usable = frameIsUsable(signals, policy);
  if (!usable.usable) return { satisfied: false, progress: 0, reason: usable.reason };

  if (kind === 'smile') {
    if (signals.smileScore === null) {
      // Never silently pass a challenge that could not be measured.
      return {
        satisfied: false,
        progress: 0,
        reason: 'smile detection is unavailable in this build',
      };
    }
    const satisfied = signals.smileScore >= policy.smileThreshold;
    return {
      satisfied,
      progress: clamp01(signals.smileScore / Math.max(policy.smileThreshold, 1e-6)),
      reason: satisfied ? 'smile detected' : 'not smiling clearly enough yet',
    };
  }

  if (signals.pose === null) {
    return { satisfied: false, progress: 0, reason: 'head orientation could not be measured' };
  }

  const { yaw, pitch } = signals.pose;
  switch (kind) {
    case 'look_left':
      // Employee-centric: their own left is a negative yaw.
      return angleCheck(-yaw, policy.minChallengeYawDegrees, 'turn further to your left');
    case 'look_right':
      return angleCheck(yaw, policy.minChallengeYawDegrees, 'turn further to your right');
    case 'look_up':
      return angleCheck(pitch, policy.minChallengePitchDegrees, 'tilt your head up a little more');
    case 'look_down':
      return angleCheck(
        -pitch,
        policy.minChallengePitchDegrees,
        'tilt your head down a little more',
      );
    default:
      return { satisfied: false, progress: 0, reason: 'unknown challenge' };
  }
}

function angleCheck(measured: number, required: number, nudge: string): ChallengeCheck {
  const satisfied = measured >= required;
  return {
    satisfied,
    progress: clamp01(measured / Math.max(required, 1e-6)),
    reason: satisfied ? 'position reached' : nudge,
  };
}

/** True when the head has returned close enough to neutral to begin the next challenge. */
export function isNeutral(
  signals: ChallengeSignals,
  thresholds: ThresholdPolicy,
  policy: EnrolmentPolicy,
): boolean {
  const usable = frameIsUsable(signals, policy);
  if (!usable.usable || signals.pose === null) return false;
  return (
    Math.abs(signals.pose.yaw) <= thresholds.yawForwardMax &&
    Math.abs(signals.pose.pitch) <= Math.max(thresholds.pitchUpMax, thresholds.pitchDownMax)
  );
}

// ---------------------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------------------

export type LivenessPhase =
  | 'awaiting_challenge'
  | 'holding'
  | 'return_to_neutral'
  /** Challenges done; collecting near-frontal frames for the face template. */
  | 'capture_template'
  | 'passed'
  | 'failed';

/** True when the pose is inside the frontal envelope used for template and verification. */
export function isFrontal(pose: HeadPose | null, policy: EnrolmentPolicy): boolean {
  if (pose === null) return false;
  return (
    Math.abs(pose.yaw) <= policy.frontalYawMaxDegrees &&
    Math.abs(pose.pitch) <= policy.frontalPitchMaxDegrees
  );
}

export interface LivenessStatus {
  readonly phase: LivenessPhase;
  readonly challengeIndex: number;
  readonly challengeCount: number;
  readonly prompt: ChallengePrompt | null;
  /** 0..1 toward satisfying the current challenge. */
  readonly progress: number;
  /** 0..1 of the required hold duration accumulated so far. */
  readonly holdProgress: number;
  readonly message: string;
  readonly passed: boolean;
  readonly failed: boolean;
  readonly failureReason: string | null;
  /** Monotonic times at which each challenge was satisfied. Liveness evidence, not template. */
  readonly captureTimestamps: readonly number[];
  /**
   * Monotonic times of near-frontal frames captured for the face template.
   *
   * These, not `captureTimestamps`, are the frames to embed.
   */
  readonly templateTimestamps: readonly number[];
  /** How many template frames are still needed. */
  readonly templateRemaining: number;
}

export interface LivenessSessionOptions {
  /**
   * Challenge order. **Must come from the server**, randomised per attempt. A client choosing
   * its own order would let an attacker pick a sequence they had already recorded.
   */
  challenges: readonly ChallengeKind[];
  policy: EnrolmentPolicy;
  thresholds: ThresholdPolicy;
  /** Calibrated neutral pose; angles are measured relative to it when supplied. */
  neutral?: HeadPose | null;
}

export class LivenessSession {
  private readonly challenges: readonly ChallengeKind[];
  private readonly policy: EnrolmentPolicy;
  private readonly thresholds: ThresholdPolicy;
  private readonly neutral: HeadPose | null;

  private index = 0;
  private phase: LivenessPhase = 'awaiting_challenge';
  private holdStartedAt: number | null = null;
  private challengeStartedAt: number | null = null;
  private sessionStartedAt: number | null = null;
  private failureReason: string | null = null;
  private lastProgress = 0;
  private lastReason = '';
  private lastNow = 0;
  private captures: number[] = [];
  private templateCaptures: number[] = [];

  constructor(options: LivenessSessionOptions) {
    if (options.challenges.length === 0) {
      throw new Error('a liveness session needs at least one challenge');
    }
    this.challenges = [...options.challenges];
    this.policy = options.policy;
    this.thresholds = options.thresholds;
    this.neutral = options.neutral ?? null;
  }

  get currentKind(): ChallengeKind | null {
    return this.challenges[this.index] ?? null;
  }

  get isComplete(): boolean {
    return this.phase === 'passed' || this.phase === 'failed';
  }

  observe(rawSignals: ChallengeSignals): LivenessStatus {
    this.lastNow = rawSignals.monotonicTs;
    if (this.isComplete) return this.status();

    const now = rawSignals.monotonicTs;
    if (this.sessionStartedAt === null) this.sessionStartedAt = now;
    if (this.challengeStartedAt === null) this.challengeStartedAt = now;

    // A hard overall cap bounds how long an attacker has to fetch and cue up a recording.
    if (now - this.sessionStartedAt > this.policy.sessionTimeoutSeconds) {
      return this.fail('enrolment took too long; please start again');
    }

    // Angles are expressed relative to the calibrated neutral pose, so a naturally off-centre
    // seating position does not make one direction impossible to satisfy.
    const signals: ChallengeSignals =
      this.neutral === null || rawSignals.pose === null
        ? rawSignals
        : { ...rawSignals, pose: offsetPose(rawSignals.pose, this.neutral) };

    if (this.phase === 'return_to_neutral') {
      if (isNeutral(signals, this.thresholds, this.policy)) {
        this.phase = 'awaiting_challenge';
        this.challengeStartedAt = now;
        this.holdStartedAt = null;
        return this.status();
      }
      return this.status('face forward again to continue');
    }

    if (this.phase === 'capture_template') return this.collectTemplateFrame(signals, now);

    const kind = this.currentKind;
    if (kind === null) return this.beginTemplateCapture(now);

    if (now - (this.challengeStartedAt ?? now) > this.policy.challengeTimeoutSeconds) {
      return this.fail(`could not confirm "${CHALLENGE_PROMPTS[kind].title}" in time`);
    }

    const check = verifyChallenge(kind, signals, this.policy);
    this.lastProgress = check.progress;
    this.lastReason = check.reason;

    if (!check.satisfied) {
      // Losing the pose resets the hold: a challenge must be held continuously rather than
      // accumulated across separate attempts.
      this.holdStartedAt = null;
      this.phase = 'awaiting_challenge';
      return this.status();
    }

    if (this.holdStartedAt === null) this.holdStartedAt = now;
    this.phase = 'holding';

    if (now - this.holdStartedAt >= this.policy.holdSeconds) {
      this.captures.push(now);
      this.index += 1;
      this.holdStartedAt = null;
      this.challengeStartedAt = now;
      if (this.index >= this.challenges.length) return this.beginTemplateCapture(now);
      this.phase = this.policy.requireNeutralBetween ? 'return_to_neutral' : 'awaiting_challenge';
      return this.status(
        this.policy.requireNeutralBetween ? 'face forward again to continue' : undefined,
      );
    }

    return this.status();
  }

  private beginTemplateCapture(now: number): LivenessStatus {
    // Skip straight to passed if no template frames are wanted, so the session still works
    // for a re-verification check that only needs liveness.
    if (this.policy.templateFrameCount <= 0) return this.pass();
    this.phase = 'capture_template';
    this.challengeStartedAt = now;
    return this.status('look straight at the camera and hold still');
  }

  /**
   * Collect near-frontal frames for the template.
   *
   * Only frontal frames are accepted, and they are spaced apart in time so the set describes
   * the face across a moment or two rather than five copies of one instant.
   */
  private collectTemplateFrame(signals: ChallengeSignals, now: number): LivenessStatus {
    if (now - (this.challengeStartedAt ?? now) > this.policy.challengeTimeoutSeconds) {
      // Some template frames are better than none: proceed if we have any at all.
      if (this.templateCaptures.length > 0) return this.pass();
      return this.fail('could not capture a clear, front-facing image in time');
    }

    const usable = frameIsUsable(signals, this.policy);
    if (!usable.usable) return this.status(usable.reason);

    if (!isFrontal(signals.pose, this.policy)) {
      return this.status('look straight at the camera');
    }

    const last = this.templateCaptures[this.templateCaptures.length - 1];
    if (last !== undefined && now - last < this.policy.templateFrameIntervalSeconds) {
      return this.status('hold still...');
    }

    this.templateCaptures.push(now);
    if (this.templateCaptures.length >= this.policy.templateFrameCount) return this.pass();
    return this.status('hold still...');
  }

  private pass(): LivenessStatus {
    this.phase = 'passed';
    return this.status('liveness confirmed');
  }

  private fail(reason: string): LivenessStatus {
    this.phase = 'failed';
    this.failureReason = reason;
    return this.status(reason);
  }

  reset(): void {
    this.index = 0;
    this.phase = 'awaiting_challenge';
    this.holdStartedAt = null;
    this.challengeStartedAt = null;
    this.sessionStartedAt = null;
    this.failureReason = null;
    this.captures = [];
    this.templateCaptures = [];
    this.lastProgress = 0;
    this.lastReason = '';
  }

  private status(message?: string): LivenessStatus {
    const kind = this.currentKind;
    const prompt = kind === null ? null : CHALLENGE_PROMPTS[kind];
    const holdProgress =
      this.holdStartedAt === null || this.policy.holdSeconds <= 0
        ? 0
        : clamp01((this.lastNow - this.holdStartedAt) / this.policy.holdSeconds);

    return {
      phase: this.phase,
      challengeIndex: Math.min(this.index, Math.max(0, this.challenges.length - 1)),
      challengeCount: this.challenges.length,
      prompt,
      progress: this.lastProgress,
      holdProgress,
      message:
        message ??
        (this.phase === 'holding' ? 'hold it...' : this.lastReason || prompt?.instruction || ''),
      passed: this.phase === 'passed',
      failed: this.phase === 'failed',
      failureReason: this.failureReason,
      captureTimestamps: [...this.captures],
      templateTimestamps: [...this.templateCaptures],
      templateRemaining: Math.max(
        0,
        this.policy.templateFrameCount - this.templateCaptures.length,
      ),
    };
  }
}

/**
 * Draw a randomised challenge sequence.
 *
 * This belongs on the server. It is exported so it can be unit-tested and so a dev harness can
 * run without a backend, but a production client must never call it: choosing your own
 * challenges defeats the point of randomising them.
 */
export function drawChallenges(
  count: number,
  random: () => number = Math.random,
  pool: readonly ChallengeKind[] = CHALLENGE_KINDS,
): ChallengeKind[] {
  const available = [...pool];
  const drawn: ChallengeKind[] = [];
  const wanted = Math.max(0, Math.min(count, available.length));
  for (let i = 0; i < wanted; i += 1) {
    const index = Math.min(available.length - 1, Math.floor(random() * available.length));
    drawn.push(available[index] as ChallengeKind);
    available.splice(index, 1);
  }
  return drawn;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}
